import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import { SortOrder } from '@cast/types';
import { ChildrenOrganizationsResponse } from '@cast/types-organization-management';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { overrideOrganizationHeader } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import {
  ENTERPRISE_ORGANIZATION_CHILDREN_QUERY_KEY,
  getChildOrganizationsQueryKey,
} from '@cast/features-organization-shared-create';

export {
  ENTERPRISE_ORGANIZATION_CHILDREN_QUERY_KEY,
  getChildOrganizationsQueryKey,
};

type ChildOrganizationQueryParams = {
  enterpriseOrgId: string;
  search?: string;
  pageLimit?: number;
  sortField?: string;
  sortOrder?: SortOrder;
};

const getChildOrganizationsQueryOptions = ({
  enterpriseOrgId,
  search,
  pageLimit = 10,
  sortField,
  sortOrder,
  enabled = true,
}: ChildOrganizationQueryParams & { enabled?: boolean }) =>
  infiniteQueryOptions({
    queryKey: getChildOrganizationsQueryKey({
      pageLimit,
      enterpriseOrgId,
      search,
      sortField,
      sortOrder,
    }),
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.enterprises.listChildrenOrganizations(
        {
          enterpriseId: enterpriseOrgId,
          pageLimit: pageLimit.toString(),
          pageCursor: pageParam,
          organizationName: search,
          sortField,
          sortOrder,
        },
        {
          headers: overrideOrganizationHeader(enterpriseOrgId),
        }
      );

      return res.data as ChildrenOrganizationsResponse;
    },
    initialPageParam: '0',
    maxPages: 1,
    getNextPageParam: ({ nextPageCursor }) =>
      nextPageCursor ? nextPageCursor : undefined,
    getPreviousPageParam: ({ prevPageCursor }) =>
      prevPageCursor ? prevPageCursor : undefined,
    enabled: makeIsEnabled(!!enterpriseOrgId, enabled),
  });

type ChildOrganizationQueryArgs = {
  enabled?: boolean;
  search?: string;
  pageLimit?: number;
  sortField?: string;
  sortOrder?: SortOrder;
};

export const useChildOrganizationsInfiniteQuery = ({
  enabled,
  search,
  pageLimit = 10,
  sortField,
  sortOrder,
}: ChildOrganizationQueryArgs) => {
  const { enterpriseOrgId } = useOrganizations();

  return useInfiniteQuery(
    getChildOrganizationsQueryOptions({
      enterpriseOrgId: enterpriseOrgId as string,
      search,
      pageLimit,
      sortField,
      sortOrder,
      enabled,
    })
  );
};
